import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import botIcon from '../../Optibot Darker Logo.svg';
import { Copy, Check } from 'lucide-react';
import '../../App.css'; 

const BotMessage = (props) => {
  const { content, loading, loadingStage } = props;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const getLoadingText = (stage) => {
    switch (stage) {
      case 'connecting':
        return 'Connecting to server...';
      case 'processing':
        return 'Processing your request...';
      case 'generating':
        return 'Generating response...';
      default:
        return 'Processing your request...';
    }
  };

  const renderLoadingIndicator = () => (
    <div className="loading-container">
      <div className="typing-dots">
        <div className="dot"></div>
        <div className="dot"></div>
        <div className="dot"></div>
      </div>
      <span className="loading-text">{getLoadingText(loadingStage)}</span>
      {loading && <div className="streaming-progress"></div>}
    </div>
  );

  // Function to highlight key contract fields
  const highlightKeyFields = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Key fields to highlight in contract details - ordered by specificity (most specific first)
    const keyFields = [
      'Estimated Contract Value:',
      'Period of Performance:',
      'Solicitation Number:',
      'NAICS Description:',
      'Last Modified Date:',
      'Relevant Keywords:',
      'Point of Contact:',
      'Contract Details:',
      'Contract Title:',
      'Set-Aside Type:',
      'Agency Name:',
      'NAICS Code:',
      'Award Date:',
      'Website:',
      'Description:' // This should be last to avoid conflicts with "NAICS Description:"
    ];
    
    let highlightedText = text;
    
    // Process fields in order, using precise matching
    keyFields.forEach(field => {
      // Create a pattern that matches the field at the start of a line or after whitespace
      // and is followed by a space or end of line
      const pattern = `(^|\\s)(${field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})(?=\\s|$)`;
      const regex = new RegExp(pattern, 'gim');
      
      highlightedText = highlightedText.replace(regex, (match, prefix, fieldName) => {
        return `${prefix}**${fieldName}**`;
      });
    });
    
    return highlightedText;
  };

  const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={tomorrow}
          language={match[1]}
          PreTag="div"
          className="code-block"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className="inline-code" {...props}>
          {children}
        </code>
      );
    },
    table({ children }) {
      return (
        <div className="table-wrapper">
          <table className="enhanced-table">{children}</table>
        </div>
      );
    },
    thead({ children }) {
      return <thead className="table-header">{children}</thead>;
    },
    tbody({ children }) {
      return <tbody className="table-body">{children}</tbody>;
    },
    tr({ children }) {
      return <tr className="table-row">{children}</tr>;
    },
    th({ children }) {
      return <th className="table-header-cell">{children}</th>;
    },
    td({ children }) {
      return <td className="table-cell">{children}</td>;
    },
    blockquote({ children }) {
      return <blockquote className="enhanced-blockquote">{children}</blockquote>;
    },
    h1({ children }) {
      return <h1 className="message-heading-1">{children}</h1>;
    },
    h2({ children }) {
      return <h2 className="message-heading-2">{children}</h2>;
    },
    h3({ children }) {
      return <h3 className="message-heading-3">{children}</h3>;
    },
    ul({ children }) {
      return <ul>{children}</ul>;
    },
    ol({ children }) {
      return <ol>{children}</ol>;
    },
    li({ children }) {
      return <li>{children}</li>;
    },
    p({ children }) {
      return <p className="message-paragraph">{children}</p>;
    },
    a({ href, children }) {
      return (
        <a 
          href={href} 
          className="enhanced-link" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          {children}
        </a>
      );
    },
  };

  return (
    <div className="message-row bot">
      <img src={botIcon} alt="Bot" className="bot-icon" />
      <div className={`bot-message enhanced ${loading ? 'streaming' : ''}`}>
        {loading && !content ? (
          renderLoadingIndicator()
        ) : (
          <>
            <div className="message-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {highlightKeyFields(content) || '...'}
              </ReactMarkdown>
            </div>
            {content && !loading && (
              <div className="message-actions">
                <button
                  onClick={handleCopy}
                  className="copy-button"
                  title="Copy response"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BotMessage;
